import React, { useState, useEffect } from 'react';
import { FaEye, FaEdit, FaTrash, FaTools, FaSearch, FaStar, FaMapMarkerAlt, FaCheckCircle, FaTimesCircle, FaSpinner, FaUser, FaEnvelope, FaPhone, FaLock, FaIdCard, FaCog, FaCopy, FaChevronDown, FaChevronUp, FaClock, FaFileAlt } from 'react-icons/fa';
import { userService, mechanicGarageService, getDocumentUrl } from '../../../../services/apiService';
import AdminLocationDropdown from '../components/AdminLocationDropdown';
import { getDisplayRole, getProfileDisplayEmail, getProfileDisplayName } from '../../../../utils/displayName';
import { toast } from 'react-toastify';

const isDocumentUrl = (v) => {
    if (v == null) return false;
    if (typeof v === 'string') return /^\/uploads\//.test(v) || /^uploads\//.test(v) || /^https?:\/\//.test(v);
    if (typeof v === 'object' && v !== null && typeof v.path === 'string') return true;
    return false;
};
const getDocumentValue = (v) => (v == null ? '' : typeof v === 'object' && v !== null && v.path != null ? v.path : String(v));

const DataRow = ({ label, value, icon: Icon, copyable, onCopy }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        if (value == null || value === '') return;
        const str = Array.isArray(value) ? value.join(', ') : String(value);
        if (str === '••••••••') return;
        navigator.clipboard?.writeText(str).then(() => {
            setCopied(true);
            onCopy?.();
            setTimeout(() => setCopied(false), 1500);
        });
    };
    const documentUrl = getDocumentValue(value);
    const isDoc = isDocumentUrl(value);
    const display = value == null ? '—' : value === true ? 'Yes' : value === false ? 'No' : Array.isArray(value) ? (value.length ? value.join(', ') : '—') : isDoc ? 'View document' : String(value);
    const showCopy = copyable && display && display !== '—' && display !== '••••••••' && !isDoc;
    return (
        <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0 group">
            {Icon && <div className="mt-0.5 w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 shrink-0 group-hover:bg-gray-200 transition-colors"><Icon className="text-sm" /></div>}
            <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
                {isDoc && documentUrl ? (
                    <a href={getDocumentUrl(documentUrl)} target="_blank" rel="noopener noreferrer" className="mt-0.5 text-sm font-medium text-sky-600 hover:underline break-words inline-flex items-center gap-1">
                        <FaFileAlt className="text-xs shrink-0" /> View document
                    </a>
                ) : (
                    <p className="mt-0.5 text-sm font-medium text-gray-900 break-words">{display}</p>
                )}
            </div>
            {showCopy && (
                <button type="button" onClick={handleCopy} className="mt-0.5 p-2 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-all shrink-0" title="Copy">
                    <FaCopy className={`text-xs ${copied ? 'text-amber-500' : ''}`} />
                </button>
            )}
        </div>
    );
};

const Mechanics = () => {
    const [view, setView] = useState('list'); // 'list', 'edit', 'view'
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMechanic, setSelectedMechanic] = useState(null);
    const [mechanics, setMechanics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [expandedAccount, setExpandedAccount] = useState(true);
    const [expandedMechanic, setExpandedMechanic] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [locationFilter, setLocationFilter] = useState(() => {
        try { return localStorage.getItem('adminLocation') || 'all'; } catch (e) { return 'all'; }
    });

    const [formData, setFormData] = useState({
        name: '', email: '', specialization: '', location: '', phone: '', password: '', status: 'Active'
    });

    // Fetch mechanics from API
    useEffect(() => {
        fetchMechanics();
    }, []);

    const fetchMechanics = async () => {
        try {
            setLoading(true);
            const response = await userService.getAllUsers();
            const users = response.data || response.users || response;
            const mechanicsData = Array.isArray(users)
                ? users.filter(u => (u.role || '').toLowerCase() === 'mechanics').map(user => ({
                    id: user._id || user.id,
                    _id: user._id || user.id,
                    name: user.mechanicDetails?.name || getProfileDisplayName(user) || '',
                    email: getProfileDisplayEmail(user) || '',
                    phone: user.phone || '',
                    phoneCode: user.phoneCode || '+91',
                    role: user.role,
                    kycStatus: user.kycStatus || user.mechanicDetails?.kycStatus || 'Pending',
                    status: user.isActive !== undefined ? (user.isActive ? 'Active' : 'Inactive') : (user.status || 'Active'),
                    mechanicDetails: user.mechanicDetails,
                    specialization: user.mechanicDetails?.specialization || '',
                    location: user.mechanicDetails?.location || user.location || '',
                    createdAt: user.createdAt
                }))
                : [];
            setMechanics(mechanicsData);
        } catch (error) {
            console.error('Failed to fetch mechanics:', error);
            toast.error('Failed to load mechanics: ' + (error.message || ''));
        } finally {
            setLoading(false);
        }
    };

    // Expand sections when opening a mechanic profile
    useEffect(() => {
        if (view === 'view' && selectedMechanic) {
            setExpandedAccount(true);
            setExpandedMechanic(true);
        }
    }, [view, selectedMechanic?.id, selectedMechanic?._id]);

    const uniqueLocations = [...new Set(mechanics.map(m => (m.location || '').trim()).filter(Boolean))].sort();

    const filteredMechanics = mechanics.filter(m => {
        const matchesSearch = !searchTerm ||
            m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            m.specialization?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (m.location && m.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (m.phone && m.phone.includes(searchTerm));
        const statusNorm = (m.status || 'Active').toString().trim().toLowerCase();
        const filterNorm = statusFilter.toString().trim().toLowerCase();
        const matchesStatus = statusFilter === 'all' || filterNorm === statusNorm;
        const matchesLocation = locationFilter === 'all' || (m.location || '').trim() === locationFilter;
        return matchesSearch && matchesStatus && matchesLocation;
    });

    const handleClearFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setLocationFilter('all');
        toast.info('Filters cleared');
    };

    const handleKycStatusChange = async (mechanic, newKycStatus) => {
        const id = mechanic.id || mechanic._id;
        try {
            await mechanicGarageService.updateMechanic(id, { kycStatus: newKycStatus });
            setMechanics(prev => prev.map(m => (m.id === id || m._id === id) ? { ...m, kycStatus: newKycStatus } : m));
            toast.success('KYC status updated');
        } catch (err) {
            toast.error(err.message || 'Failed to update KYC status');
        }
    };

    const handleAction = (type, mechanic) => {
        setSelectedMechanic(mechanic);
        if (type === 'view') {
            setView('view');
        } else if (type === 'edit') {
            setFormData({
                name: mechanic.name || '',
                email: mechanic.email || '',
                specialization: mechanic.specialization || '',
                location: mechanic.location || '',
                phone: mechanic.phone || '',
                password: '', // Don't show existing password
                status: mechanic.status || 'Active'
            });
            setView('edit');
        } else if (type === 'delete') {
            if (window.confirm(`Are you sure you want to remove ${getProfileDisplayName(mechanic) || mechanic.mechanicDetails?.name || mechanic.name || 'this mechanic'}?`)) {
                handleDelete(mechanic.id || mechanic._id);
            }
        }
    };

    const handleDelete = async (mechanicId) => {
        try {
            setLoading(true);
            await mechanicGarageService.deleteMechanic(mechanicId);
            toast.success('Mechanic deleted successfully!');
            fetchMechanics();
        } catch (error) {
            console.error('Failed to delete mechanic:', error);
            toast.error('Failed to delete mechanic: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            
            if (view === 'edit' && selectedMechanic) {
                // Update existing mechanic
                const mechanicId = selectedMechanic.id || selectedMechanic._id;
                // Only send password if it's provided (not empty)
                const updateData = { ...formData };
                if (!updateData.password || updateData.password.trim() === '') {
                    delete updateData.password; // Don't send password if empty - keep existing password
                }
                await mechanicGarageService.updateMechanic(mechanicId, updateData);
                toast.success('Mechanic updated successfully!');
                
                // Reset form and go back to list
                setView('list');
                setFormData({ name: '', email: '', specialization: '', location: '', phone: '', password: '', status: 'Active' });
                setSelectedMechanic(null);
                fetchMechanics();
            }
        } catch (error) {
            console.error('Failed to save mechanic:', error);
            toast.error('Failed to save mechanic: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    if (view === 'edit') {
        return (
            <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex items-center justify-between gap-4">
                    <h1 className="text-xl font-bold text-gray-900 tracking-tight">Edit Mechanic</h1>
                    <button type="button" onClick={() => setView('list')} className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors">← Back</button>
                </div>

                <form onSubmit={handleSave} className="space-y-6">
                    <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-shadow hover:shadow-md">
                        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/80">
                            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                <FaTools className="text-gray-500" /> Account & mechanic details
                            </h3>
                            <p className="text-xs text-gray-500 mt-0.5">Update basic info, skills and status</p>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Mechanic name *</label>
                                    <input required type="text" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Enter mechanic name" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email *</label>
                                    <input required type="email" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="mechanic@example.com" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Phone</label>
                                    <input type="text" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 1234567890" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Specialization</label>
                                    <input type="text" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition" value={formData.specialization} onChange={e => setFormData({ ...formData, specialization: e.target.value })} placeholder="e.g., Engine Specialist" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Location</label>
                                    <input type="text" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition" value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} placeholder="e.g., Mumbai" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Status</label>
                                    <select className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white" value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                        <option value="Suspended">Suspended</option>
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Password (leave empty to keep existing)</label>
                                    <input type="password" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} placeholder="Leave empty to keep existing password" />
                                    <p className="text-xs text-gray-500 mt-1">Only enter a new password if you want to change it.</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <button type="submit" disabled={saving} className="flex-1 py-3 px-4 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 hover:scale-[1.01] active:scale-[0.99] transition-all shadow-sm disabled:opacity-50 flex items-center justify-center gap-2">
                            {saving && <FaSpinner className="animate-spin" />}
                            Update mechanic
                        </button>
                        <button type="button" onClick={() => setView('list')} className="flex-1 py-3 px-4 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 active:scale-[0.99] transition-all">
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    const getProfileImage = (m) => (m?.logo || m?.avatar || m?.profilePhoto || m?.image || m?.mechanicDetails?.logo || m?.mechanicDetails?.storeLogo || '');

    const renderDetails = (obj, expandNested = false) => {
        if (!obj || typeof obj !== 'object') return null;
        const entries = Object.entries(obj).filter(([k]) => k !== 'password' && k !== 'passwordHash');
        if (entries.length === 0) return null;
        const format = (v) => {
            if (v === undefined || v === null) return '';
            if (v === true) return 'Yes';
            if (v === false) return 'No';
            if (Array.isArray(v)) return v.length ? v.join(', ') : '';
            if (typeof v === 'object' && !expandNested) return JSON.stringify(v);
            return String(v);
        };
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {entries.map(([k, v]) => (
                    <div key={k} className="p-4 bg-gray-50 rounded-2xl">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{String(k).replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()}</p>
                        {expandNested && typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length > 0 ? (
                            <div className="mt-2 space-y-2">{renderDetails(v, true)}</div>
                        ) : (
                            <p className="font-bold text-gray-900 break-words mt-1">{format(v)}</p>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    if (view === 'view' && selectedMechanic) {
        const md = selectedMechanic.mechanicDetails || {};
        const displayName = md.name || '';
        const displayEmail = getProfileDisplayEmail(selectedMechanic);
        const fullPhone = (selectedMechanic.phoneCode || '') + (selectedMechanic.phone || '');
        const handleContact = () => {
            if (displayEmail) window.location.href = `mailto:${displayEmail}`;
            else if (fullPhone) window.location.href = `tel:${fullPhone.replace(/\s/g, '')}`;
            else toast.info('No contact details available');
        };
        const specialization = md.specialization || selectedMechanic.specialization;
        const location = md.location || selectedMechanic.location;

        return (
            <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex items-center justify-between gap-4">
                    <h1 className="text-xl font-bold text-gray-900 tracking-tight">Mechanic Profile</h1>
                    <button onClick={() => setView('list')} className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors">← Back</button>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-shadow hover:shadow-md">
                    <div className="p-6 sm:p-8 flex flex-col sm:flex-row gap-6">
                        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl flex items-center justify-center text-white shrink-0 overflow-hidden bg-gradient-to-br from-amber-500 to-amber-600 ring-2 ring-white ring-offset-2 shadow-lg">
                            {getProfileImage(selectedMechanic) ? (
                                <img loading="lazy" src={getProfileImage(selectedMechanic)} alt={displayName} className="w-full h-full object-cover" />
                            ) : (
                                <FaTools className="w-10 h-10 sm:w-12 sm:h-12 text-amber-200" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-2xl font-bold text-gray-900 truncate">{displayName}</h2>
                            {displayEmail ? (
                                <a href={`mailto:${displayEmail}`} className="mt-1 block text-sm text-slate-600 hover:text-slate-800 hover:underline truncate transition-colors">{displayEmail}</a>
                            ) : null}
                            {(selectedMechanic.phone || selectedMechanic.phoneCode) && (
                                <a href={`tel:${fullPhone.replace(/\s/g, '')}`} className="mt-0.5 inline-block text-sm text-slate-600 hover:text-slate-800 hover:underline transition-colors">
                                    {(selectedMechanic.phoneCode || '') + ' ' + (selectedMechanic.phone || '')}
                                </a>
                            )}
                            <div className="mt-3 flex flex-wrap gap-2">
                                {specialization && (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-amber-100 text-amber-800">{specialization}</span>
                                )}
                                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700">{selectedMechanic.role || 'mechanics'}</span>
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${(selectedMechanic.status || '').toLowerCase() === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                    {selectedMechanic.status ?? '—'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-shadow hover:shadow-md">
                    <button type="button" onClick={() => setExpandedAccount((e) => !e)} className="w-full px-6 py-4 border-b border-gray-100 bg-gray-50/80 flex items-center justify-between gap-2 text-left hover:bg-gray-100/80 transition-colors">
                        <div className="flex items-center gap-2">
                            <FaIdCard className="text-gray-500" />
                            <h3 className="text-sm font-semibold text-gray-900">Account Information</h3>
                        </div>
                        <span className="text-gray-400">{expandedAccount ? <FaChevronUp /> : <FaChevronDown />}</span>
                    </button>
                    {expandedAccount && (
                        <div className="px-6 py-4 divide-y divide-gray-100">
                            <DataRow label="Name" value={md.name || ''} icon={FaUser} copyable onCopy={() => toast.success('Name copied')} />
                            <DataRow label="Email" value={displayEmail || '—'} icon={FaEnvelope} copyable onCopy={() => toast.success('Email copied')} />
                            <DataRow label="Password" value="••••••••" icon={FaLock} />
                            <DataRow label="Phone" value={selectedMechanic.phone || '—'} icon={FaPhone} copyable onCopy={() => toast.success('Phone copied')} />
                            <DataRow label="Phone code" value={selectedMechanic.phoneCode || (selectedMechanic.phone ? '+91' : '') || '—'} icon={FaPhone} copyable onCopy={() => toast.success('Phone code copied')} />
                            <DataRow label="Role" value={getDisplayRole(selectedMechanic) || selectedMechanic.role || '—'} icon={FaCog} copyable onCopy={() => toast.success('Role copied')} />
                        </div>
                    )}
                </section>

                <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-shadow hover:shadow-md">
                    <button type="button" onClick={() => setExpandedMechanic((e) => !e)} className="w-full px-6 py-4 border-b border-gray-100 bg-gray-50/80 flex items-center justify-between gap-2 text-left hover:bg-gray-100/80 transition-colors">
                        <div className="flex items-center gap-2">
                            <FaTools className="text-gray-500" />
                            <h3 className="text-sm font-semibold text-gray-900">Mechanic Information</h3>
                        </div>
                        <span className="text-gray-400">{expandedMechanic ? <FaChevronUp /> : <FaChevronDown />}</span>
                    </button>
                    {expandedMechanic && (
                        <div className="px-6 py-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                                <div className="space-y-0 divide-y divide-gray-100">
                                    <DataRow label="Name" value={md.name || ''} icon={FaUser} copyable onCopy={() => toast.success('Name copied')} />
                                    <DataRow label="Photo" value={md.photo || getProfileImage(selectedMechanic) || null} icon={FaTools} />
                                    <DataRow label="Date of birth" value={md.dob} icon={FaUser} copyable onCopy={() => toast.success('Copied')} />
                                    <DataRow label="Experience (years)" value={md.experienceYears} icon={FaTools} copyable onCopy={() => toast.success('Copied')} />
                                </div>
                                <div className="space-y-0 sm:border-l sm:border-gray-100 sm:pl-6 divide-y divide-gray-100">
                                    <DataRow label="Specialization" value={specialization} icon={FaTools} copyable onCopy={() => toast.success('Copied')} />
                                    <DataRow label="Location" value={location} icon={FaMapMarkerAlt} copyable onCopy={() => toast.success('Copied')} />
                                    <DataRow label="Availability" value={md.availability} icon={FaClock} copyable onCopy={() => toast.success('Copied')} />
                                </div>
                            </div>
                            <div className="mt-2 pt-2 border-t border-gray-100">
                                <DataRow label="Bio" value={md.bio} icon={FaTools} copyable onCopy={() => toast.success('Copied')} />
                            </div>
                        </div>
                    )}
                </section>

                <div className="flex flex-col sm:flex-row gap-3">
                    <button onClick={() => handleAction('edit', selectedMechanic)} className="flex-1 py-3 px-4 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 hover:scale-[1.01] active:scale-[0.99] transition-all shadow-sm">
                        Edit mechanic
                    </button>
                    <button onClick={handleContact} className="flex-1 py-3 px-4 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 active:scale-[0.99] transition-all">
                        Send message
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-xl font-bold text-gray-900 tracking-tight">Mechanic Management</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Verify and manage field experts</p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div className="relative lg:col-span-2">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                        <input
                            type="text"
                            placeholder="Search by name, specialization, location or phone..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
                    >
                        <option value="all">All status</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Suspended">Suspended</option>
                    </select>
                    <AdminLocationDropdown
                        value={locationFilter === 'all' ? '' : locationFilter}
                        onChange={(v) => {
                            const val = v || 'all';
                            setLocationFilter(val);
                            try {
                                if (val === 'all') {
                                    localStorage.removeItem('adminLocation');
                                } else if (typeof v === 'object') {
                                    localStorage.setItem('adminLocation', JSON.stringify(v));
                                } else {
                                    localStorage.setItem('adminLocation', String(v));
                                }
                            } catch (e) {}
                        }}
                        className="w-full"
                    />
                    <button
                        type="button"
                        onClick={handleClearFilters}
                        disabled={!searchTerm && statusFilter === 'all' && locationFilter === 'all'}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                        <FaTimesCircle /> Clear filters
                    </button>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                    Showing {filteredMechanics.length} of {mechanics.length} mechanic{mechanics.length !== 1 ? 's' : ''}
                </p>
            </div>

            {loading && (
                <div className="flex justify-center items-center py-12 bg-white rounded-xl border border-gray-200">
                    <FaSpinner className="animate-spin text-amber-600 text-3xl" />
                </div>
            )}

            {!loading && filteredMechanics.length === 0 && (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                    <FaTools className="mx-auto text-gray-300 text-4xl mb-3" />
                    <p className="text-gray-600 font-medium">No mechanics found</p>
                    <p className="text-sm text-gray-500 mt-1">
                        {mechanics.length === 0 ? 'No mechanics in the system yet.' : 'Try adjusting search or filters.'}
                    </p>
                    {(searchTerm || statusFilter !== 'all' || locationFilter !== 'all') && (
                        <button type="button" onClick={handleClearFilters} className="mt-4 text-sm font-medium text-amber-600 hover:text-amber-700">
                            Clear filters
                        </button>
                    )}
                </div>
            )}

            {!loading && filteredMechanics.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Mechanic</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Specialization</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Location</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">KYC Status</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredMechanics.map((mechanic) => (
                                    <tr key={mechanic.id || mechanic._id} className="hover:bg-gray-50/50 transition">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center text-white shadow-sm shrink-0">
                                                    <FaTools className="text-sm" />
                                                </div>
                                                <div className="min-w-0">
                                                    <span className="text-sm font-semibold text-gray-900 block truncate">
                                                {mechanic.mechanicDetails?.name || getProfileDisplayName(mechanic) || ''}
                                              </span>
                                                    <span className="text-xs text-gray-500">{mechanic.phone || ''}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm text-gray-700">{getProfileDisplayEmail(mechanic) || ''}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {mechanic.specialization ? (
                                                <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-md bg-amber-50 text-amber-800">{mechanic.specialization}</span>
                                            ) : (
                                                <span className="text-sm text-gray-400"></span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm text-gray-700">{mechanic.location || ''}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <select
                                                value={mechanic.kycStatus || 'Pending'}
                                                onChange={(e) => handleKycStatusChange(mechanic, e.target.value)}
                                                className={`text-xs font-medium rounded-lg px-2.5 py-1.5 border cursor-pointer ${(mechanic.kycStatus || 'Pending') === 'Verified' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : (mechanic.kycStatus || 'Pending') === 'Rejected' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}
                                            >
                                                <option value="Pending">Pending</option>
                                                <option value="Verified">Verified</option>
                                                <option value="Rejected">Rejected</option>
                                            </select>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${(mechanic.status || 'Active').toString().toLowerCase() === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                                                {mechanic.status || 'Active'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={() => handleAction('view', mechanic)} className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition" title="View"><FaEye /></button>
                                                <button onClick={() => handleAction('edit', mechanic)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition" title="Edit"><FaEdit /></button>
                                                <button onClick={() => handleAction('delete', mechanic)} disabled={loading} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50" title="Delete"><FaTrash /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Mechanics;

